import { normalizeLoginEmail } from '../utils/productionLogin.js'

/** Soft cap for PetBusiness workspace seats (active + pending). */
export const MAX_WORKSPACE_MEMBERS = 10

/**
 * Isolated Playwright / API-test hotel. Never the production Z Pet Hotels or
 * Reliable Pet Care workspaces.
 */
export const TEST_HOTEL_EMAIL = 'playwright@doggos.test'
export const TEST_HOTEL_ACCOUNT_ID = 'doggos-playwright'
export const TEST_HOTEL_NAME = 'Doggos Test Hotel'

/** Workspace roles on the account roster (not platform doggosRole). */
export const WORKSPACE_ROLES = Object.freeze({
  admin: 'admin',
  member: 'member',
})

/**
 * Listing email → account/listing id for bootstrap when Stream user has no
 * accountId yet. Keys must be normalizeLoginEmail() form.
 * Mirrors MDX listing frontmatter for seeded managers.
 */
export const LISTING_ACCOUNT_BY_EMAIL = Object.freeze({
  'manager@zpethotels.com': 'z-pet-hotels',
  'jacqueline@doggos.com': 'reliable-pet-care',
  [TEST_HOTEL_EMAIL]: TEST_HOTEL_ACCOUNT_ID,
})

/**
 * @param {string | null | undefined} email
 * @returns {string | null}
 */
export function listingAccountIdForEmail(email) {
  const key = normalizeLoginEmail(email)
  if (!key) return null
  return LISTING_ACCOUNT_BY_EMAIL[key] || null
}

/**
 * Seeded listing-owner emails for an account (e.g. manager@zpethotels.com
 * on z-pet-hotels). Used so confirm mail still reaches the workspace owner
 * if the Stream roster is empty or missing them.
 *
 * @param {string | null | undefined} accountId
 * @returns {string[]}
 */
export function seededOwnerEmailsForAccountId(accountId) {
  const id = String(accountId || '').trim()
  if (!id) return []
  return Object.entries(LISTING_ACCOUNT_BY_EMAIL)
    .filter(([, listingId]) => listingId === id)
    .map(([email]) => email)
}

/**
 * Seeded account owner for a workspace (e.g. manager@zpethotels.com on
 * z-pet-hotels). That person cannot be removed from their workspace roster.
 *
 * @param {{ email?: string | null, accountId?: string | null }} params
 * @returns {boolean}
 */
export function isProtectedWorkspaceOwner({ email, accountId } = {}) {
  const normalizedEmail = normalizeLoginEmail(email)
  const normalizedAccountId = String(accountId || '').trim()
  if (!normalizedEmail || !normalizedAccountId) return false
  return listingAccountIdForEmail(normalizedEmail) === normalizedAccountId
}

/**
 * @param {string | null | undefined} role
 * @returns {'admin' | 'member'}
 */
export function normalizeWorkspaceRole(role) {
  return role === WORKSPACE_ROLES.admin
    ? WORKSPACE_ROLES.admin
    : WORKSPACE_ROLES.member
}

/**
 * @param {string | null | undefined} role
 * @returns {boolean}
 */
export function isValidWorkspaceRole(role) {
  return role === WORKSPACE_ROLES.admin || role === WORKSPACE_ROLES.member
}
